/* Progressive enhancements; native forms and details work without this script. */
class CmCatalogNavigation extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.dataset.enhanced = 'true';
    this.toggleButton = this.querySelector('.cm-nav-toggle');
    this.groups = [...this.querySelectorAll('.cm-nav-disclosure')];
    const desktop = () => matchMedia('(min-width: 1000px) and (hover: hover)').matches;
    this.toggleButton.addEventListener('click', () => {
      const expanded = this.toggleButton.getAttribute('aria-expanded') !== 'true';
      this.toggleButton.setAttribute('aria-expanded', String(expanded));
      this.dataset.mobileOpen = String(expanded);
    }, options);
    for (const group of this.groups) {
      group.addEventListener('pointerenter', () => {
        if (!desktop()) return;
        clearTimeout(this.leaveTimer);
        this.groups.forEach(other => { other.open = other === group; });
      }, options);
      group.addEventListener('pointerleave', () => {
        if (!desktop()) return;
        this.leaveTimer = setTimeout(() => { if (!group.contains(document.activeElement)) group.open = false; }, 140);
      }, options);
      group.addEventListener('toggle', () => {
        if (group.open) this.groups.forEach(other => { if (other !== group) other.open = false; });
      }, options);
      group.addEventListener('focusout', event => {
        if (!group.contains(event.relatedTarget)) group.open = false;
      }, options);
    }
    this.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const open = this.groups.find(group => group.open);
      if (open) { open.open = false; open.querySelector('summary').focus(); }
      else { this.dataset.mobileOpen = 'false'; this.toggleButton.setAttribute('aria-expanded', 'false'); this.toggleButton.focus(); }
    }, options);
    document.addEventListener('click', event => {
      if (!this.contains(event.target)) this.groups.forEach(group => { group.open = false; });
    }, options);
  }
  disconnectedCallback() { this.controller?.abort(); clearTimeout(this.leaveTimer); }
}

class CmProductSearch extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.input = this.querySelector('input[type=search]');
    this.results = this.querySelector('.cm-search-results');
    this.status = this.querySelector('[role=status]');
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer); this.request?.abort(); this.results.hidden = true;
      const query = this.input.value.trim();
      this.status.textContent = '';
      if (query.length < 2) return;
      this.timer = setTimeout(() => this.search(query), 220);
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') { this.request?.abort(); clearTimeout(this.timer); this.results.hidden = true; this.input.focus(); }
      if (event.key === 'ArrowDown' && !this.results.hidden) {
        const links = [...this.results.querySelectorAll('a')];
        event.preventDefault(); links[(links.indexOf(document.activeElement) + 1) % links.length]?.focus();
      }
      if (event.key === 'ArrowUp' && !this.results.hidden) {
        const links = [...this.results.querySelectorAll('a')];
        const index = links.indexOf(document.activeElement);
        event.preventDefault(); if (index <= 0) this.input.focus(); else links[index - 1].focus();
      }
    }, options);
    this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) { this.results.hidden = true; this.request?.abort(); } }, options);
    document.addEventListener('click', event => { if (!this.contains(event.target)) this.results.hidden = true; }, options);
  }
  async search(query) {
    this.request = new AbortController();
    this.status.textContent = 'Buscando productos…';
    try {
      const url = new URL(this.dataset.url + '.json', location.origin);
      url.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '5', 'resources[options][unavailable_products]': 'last' });
      const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search unavailable');
      const data = await response.json();
      if (query !== this.input.value.trim()) return;
      const products = data.resources?.results?.products || [];
      this.results.replaceChildren();
      const list = document.createElement('ul'); list.setAttribute('aria-label', 'Productos sugeridos');
      for (const product of products) {
        const href = new URL(product.url, location.origin);
        if (href.origin !== location.origin) continue;
        const item = document.createElement('li'), link = document.createElement('a');
        link.href = href.href;
        const imageURL = product.featured_image?.url || product.image;
        if (typeof imageURL === 'string') {
          const src = new URL(imageURL, location.origin);
          if (src.protocol === 'https:' || src.origin === location.origin) {
            const image = document.createElement('img'); image.src = src.href; image.alt = ''; image.width = 52; image.height = 52; link.append(image);
          }
        }
        const title = document.createElement('span'); title.textContent = product.title; link.append(title);
        item.append(link); list.append(item);
      }
      const all = document.createElement('a'), searchURL = new URL(this.querySelector('form').action);
      searchURL.search = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last' });
      all.href = searchURL.href; all.className = 'cm-search-all'; all.textContent = products.length ? 'Ver todos los resultados →' : 'Buscar en toda la tienda →';
      this.results.append(list, all); this.results.hidden = false;
      this.status.textContent = products.length ? `${products.length} productos sugeridos. Usa Tab o la flecha abajo para explorarlos.` : 'No hay sugerencias. Pulsa Intro para buscar en la tienda.';
    } catch (error) {
      if (error.name !== 'AbortError') this.status.textContent = 'Pulsa Intro para buscar en la tienda.';
    }
  }
  disconnectedCallback() { this.controller?.abort(); this.request?.abort(); clearTimeout(this.timer); }
}

class CmFuetPromo extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.dialog = this.querySelector('dialog');
    this.key = 'cm-fuet-promotion-oct08';
    const close = () => this.dialog.close();
    this.querySelector('.cm-promo-close').addEventListener('click', close, options);
    this.querySelector('.cm-promo-dismiss').addEventListener('click', close, options);
    this.querySelector('.cm-promo-reopen').addEventListener('click', () => this.show(true), options);
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) { const r=this.dialog.getBoundingClientRect(); if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom) close(); } }, options);
    this.dialog.addEventListener('close', () => { this.remember(); document.documentElement.classList.remove('cm-promo-open'); }, options);
    this.dialog.querySelector('a').addEventListener('click', () => this.remember(), options);
    // Do not auto-open in the editor or interrupt search, text input or another dialog.
    if (this.hasAttribute('data-design-mode') || document.querySelector('cm-abisales-notice')) return;
    let seen = true;
    try { seen = Date.now() - Number(localStorage.getItem(this.key) || 0) < Number(this.dataset.frequency) * 86400000; }
    catch { /* If storage is blocked, use the voluntary promotion tab only. */ }
    if (!seen) this.timer = setTimeout(() => this.show(false), Number(this.dataset.delay) * 1000);
  }
  remember() { try { localStorage.setItem(this.key, String(Date.now())); } catch {} }
  show(manual) {
    if (this.dialog.open || typeof this.dialog.showModal !== 'function') return;
    if (!manual && (document.hidden || document.querySelector('dialog[open], .cm-nav-disclosure[open], .cm-catalog-nav[data-mobile-open=true]') || document.activeElement?.matches('input,textarea,select,[contenteditable=true]'))) return;
    this.remember(); this.dialog.showModal(); document.documentElement.classList.add('cm-promo-open');
    this.querySelector('.cm-promo-close').focus();
  }
  disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); document.documentElement.classList.remove('cm-promo-open'); }
}
if (!customElements.get('cm-catalog-navigation')) customElements.define('cm-catalog-navigation', CmCatalogNavigation);
if (!customElements.get('cm-product-search')) customElements.define('cm-product-search', CmProductSearch);
if (!customElements.get('cm-fuet-promo')) customElements.define('cm-fuet-promo', CmFuetPromo);
